import { z } from 'zod';
import { SCHEDULE_KINDS, SHIFT_OVERRIDE_KINDS, SHIFT_SWAP_ACTIONS, SHIFT_SWAP_STATUSES } from '@bulk/types';
import { dateOnlySchema, idSchema, patchOf, timeHHMM } from './common';
import { shiftSettingsSchema } from './gyms';

const localizedNames = z.record(z.string(), z.string().trim().max(80));
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a colour like #2f80ed');

export const shiftDefinitionSchema = z.object({
  id: idSchema,
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  startTime: z.string(),
  endTime: z.string(),
  overnight: z.boolean().describe('Ends on the next day: the end is at or before the start'),
  minutes: z.number().int(),
  color: z.string(),
  sortOrder: z.number().int(),
  inUse: z.boolean().describe('A schedule type, a roster change, a swap or a task list uses it, so it cannot be deleted'),
});
export type ShiftDefinitionDto = z.infer<typeof shiftDefinitionSchema>;

export const createShiftDefinitionSchema = z.object({
  name: z.string().trim().min(1).max(60),
  nameLocalized: localizedNames.default({}),
  startTime: timeHHMM,
  endTime: timeHHMM.describe('At or before the start means the shift ends the next day'),
  color: hexColor.default('#2f80ed'),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type CreateShiftDefinitionInput = z.infer<typeof createShiftDefinitionSchema>;
export const updateShiftDefinitionSchema = patchOf(createShiftDefinitionSchema);
export type UpdateShiftDefinitionInput = z.infer<typeof updateShiftDefinitionSchema>;

export const scheduleTypeSchema = z.object({
  id: idSchema,
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  kind: z.enum(SCHEDULE_KINDS),
  days: z.array(idSchema.nullable()).describe('WEEKLY: Monday to Sunday. ROTATION: the cycle from its day 1. Null is a day off.'),
  assignedCount: z.number().int().describe('People on it today or later; a type with any assignment cannot be deleted'),
});
export type ScheduleTypeDto = z.infer<typeof scheduleTypeSchema>;

export const createScheduleTypeSchema = z.object({
  name: z.string().trim().min(1).max(80),
  nameLocalized: localizedNames.default({}),
  kind: z.enum(SCHEDULE_KINDS),
  days: z.array(idSchema.nullable()).min(2).max(28).describe('WEEKLY takes exactly 7 (Monday first); ROTATION 2 to 28; at least one must be a shift'),
});
export type CreateScheduleTypeInput = z.infer<typeof createScheduleTypeSchema>;
export const updateScheduleTypeSchema = patchOf(createScheduleTypeSchema);
export type UpdateScheduleTypeInput = z.infer<typeof updateScheduleTypeSchema>;

export const shiftSettingsResponseSchema = shiftSettingsSchema;
export const updateShiftSettingsSchema = patchOf(shiftSettingsSchema);
export type UpdateShiftSettingsInput = z.infer<typeof updateShiftSettingsSchema>;

export const scheduleAssignmentSchema = z.object({
  id: idSchema,
  employeeId: idSchema,
  scheduleTypeId: idSchema,
  scheduleTypeName: z.string(),
  scheduleTypeNameLocalized: z.record(z.string(), z.string()),
  kind: z.enum(SCHEDULE_KINDS),
  locationId: idSchema,
  locationName: z.string(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  cycleStart: z.string().nullable(),
  note: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});
export type ScheduleAssignmentDto = z.infer<typeof scheduleAssignmentSchema>;

export const createScheduleAssignmentSchema = z.object({
  scheduleTypeId: idSchema,
  locationId: idSchema,
  effectiveFrom: dateOnlySchema,
  cycleStart: dateOnlySchema.optional().describe("A rotation's day 1; the start date when left out. Ignored for WEEKLY."),
  note: z.string().trim().max(300).nullable().optional(),
});
export type CreateScheduleAssignmentInput = z.infer<typeof createScheduleAssignmentSchema>;

export const endScheduleAssignmentSchema = z.object({
  lastDay: dateOnlySchema.describe('The last day the current schedule is worked'),
});
export type EndScheduleAssignmentInput = z.infer<typeof endScheduleAssignmentSchema>;

export const shiftPersonSchema = z.object({
  employeeId: idSchema,
  firstName: z.string(),
  lastName: z.string(),
  position: z.string().nullable(),
  departmentId: idSchema.nullable(),
  departmentName: z.string().nullable(),
  hasLogin: z.boolean(),
  current: scheduleAssignmentSchema.nullable().describe('The schedule in force today, or the next one if none is'),
});
export type ShiftPersonDto = z.infer<typeof shiftPersonSchema>;

export const ROSTER_SOURCES = ['SCHEDULE', 'CHANGE', 'SWAP'] as const;

export const rosterQuerySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  employeeId: idSchema.optional(),
  departmentId: idSchema.optional(),
  mine: z.stringbool().optional().describe("Only the caller's own shifts, for anyone"),
});
export type RosterQuery = z.infer<typeof rosterQuerySchema>;

export const rosterShiftSchema = z.object({
  employeeId: idSchema,
  date: z.string().describe('The day the shift starts; an overnight shift ends the next day'),
  shiftDefinitionId: idSchema,
  locationId: idSchema,
  source: z.enum(ROSTER_SOURCES),
  overrideId: idSchema.nullable().describe('The roster change that added it, if one did'),
});
export type RosterShiftDto = z.infer<typeof rosterShiftSchema>;

export const rosterSchema = z.object({
  from: z.string(),
  to: z.string(),
  today: z.string().describe("Today in the gym's time zone"),
  time: z.string().describe("The time now in the gym's time zone, HH:MM; a shift that has started can no longer be offered"),
  definitions: z.array(shiftDefinitionSchema),
  people: z.array(
    z.object({
      employeeId: idSchema,
      name: z.string(),
      departmentId: idSchema.nullable(),
      departmentName: z.string().nullable(),
      mine: z.boolean(),
    }),
  ),
  shifts: z.array(rosterShiftSchema),
  leave: z
    .array(
      z.object({
        employeeId: idSchema,
        startDate: z.string(),
        endDate: z.string(),
        typeHidden: z.boolean().describe('The caller sees only that the person is off, as on the leave calendar'),
        leaveTypeKey: z.string().nullable(),
        leaveTypeName: z.string().nullable(),
        leaveTypeNameLocalized: z.record(z.string(), z.string()).nullable(),
      }),
    )
    .describe('Approved leave in the window; it hides the shifts on those days'),
});
export type RosterDto = z.infer<typeof rosterSchema>;

export const createShiftOverrideSchema = z.object({
  employeeId: idSchema,
  date: dateOnlySchema,
  kind: z.enum(SHIFT_OVERRIDE_KINDS),
  shiftDefinitionId: idSchema,
  locationId: idSchema.optional().describe('ADD only; the branch of their schedule that day when left out'),
  note: z.string().trim().max(300).nullable().optional(),
});
export type CreateShiftOverrideInput = z.infer<typeof createShiftOverrideSchema>;

export const shiftSwapSchema = z.object({
  id: idSchema,
  date: z.string(),
  shiftDefinitionId: idSchema,
  shiftName: z.string(),
  shiftNameLocalized: z.record(z.string(), z.string()),
  startTime: z.string(),
  endTime: z.string(),
  locationId: idSchema,
  locationName: z.string(),
  fromEmployeeId: idSchema,
  fromName: z.string(),
  toEmployeeId: idSchema,
  toName: z.string(),
  status: z.enum(SHIFT_SWAP_STATUSES),
  note: z.string().nullable(),
  decisionNote: z.string().nullable(),
  decidedByName: z.string().nullable(),
  createdAt: z.string(),
  respondedAt: z.string().nullable(),
  decidedAt: z.string().nullable(),
  offeredByMe: z.boolean(),
  offeredToMe: z.boolean(),
  canDecide: z.boolean().describe('The caller may approve or reject it now'),
});
export type ShiftSwapDto = z.infer<typeof shiftSwapSchema>;

export const createShiftSwapSchema = z.object({
  date: dateOnlySchema,
  shiftDefinitionId: idSchema,
  toEmployeeId: idSchema,
  note: z.string().trim().max(300).nullable().optional(),
});
export type CreateShiftSwapInput = z.infer<typeof createShiftSwapSchema>;

export const shiftSwapActionSchema = z.object({
  action: z.enum(SHIFT_SWAP_ACTIONS),
  note: z.string().trim().max(300).nullable().optional(),
});
export type ShiftSwapActionInput = z.infer<typeof shiftSwapActionSchema>;

export const SHIFT_SWAP_SCOPES = ['mine', 'approvals', 'all'] as const;
export const shiftSwapListQuerySchema = z.object({
  scope: z.enum(SHIFT_SWAP_SCOPES).default('mine'),
});

export const swapCandidatesQuerySchema = z.object({
  date: dateOnlySchema,
  shiftDefinitionId: idSchema,
});
export const swapCandidateSchema = z.object({ employeeId: idSchema, name: z.string() });
export type SwapCandidateDto = z.infer<typeof swapCandidateSchema>;

export const shiftPendingSchema = z.object({
  offeredToMe: z.number().int(),
  awaitingApproval: z.number().int().describe('Swaps the caller may decide; 0 without shifts.manage'),
});
export type ShiftPendingDto = z.infer<typeof shiftPendingSchema>;

export const SHIFT_EXTENSION_MAX_MINUTES = 360;

export const createShiftExtensionSchema = z.object({
  employeeId: idSchema,
  minutes: z.number().int().min(5).max(SHIFT_EXTENSION_MAX_MINUTES).describe("Added to the person's working time: after the end of their current duty when they are on duty, else from now; never more than 6 hours from now"),
  reason: z.string().trim().min(3).max(300),
});
export type CreateShiftExtensionInput = z.infer<typeof createShiftExtensionSchema>;

export const shiftExtensionSchema = z.object({
  id: idSchema,
  employeeId: idSchema,
  startsAt: z.string(),
  until: z.string(),
  reason: z.string(),
  grantedByUserId: idSchema.nullable(),
  createdAt: z.string(),
});
export type ShiftExtensionDto = z.infer<typeof shiftExtensionSchema>;
